import { useWakeLock } from "@livecommerce/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import type { LocalStageStream, Stage } from "amazon-ivs-web-broadcast";
import { ApiError } from "../api/client";
import { video } from "../shows/api";
import {
  alreadyAllowed,
  type DeviceFailure,
  isSelfView,
  listDevices,
  openCamera,
  openMicrophone,
} from "./devices";
import { audible, decibels, meterScale } from "./meter";
import { type PublishSnapshot, type PublishStats, readPublishStats } from "./stats";
import { browserFacts, isMobile, support, type Support } from "./support";

/**
 * Broadcasting from the browser (spec §17.3, `R-HOST-10` to `R-HOST-13`).
 *
 * > *The host broadcasts from a web browser. This is a natural fit for the chosen video mode: IVS
 * > real-time is WebRTC-based, so a browser can publish directly with no streaming software, no
 * > RTMP, and no install.*
 *
 * One hook, holding one publish connection. Everything a host has to be told about — whether their
 * browser can do this at all, whether the camera and microphone are working, whether the upstream
 * is keeping up, whether the tab is about to be suspended — comes out of here as a fact, and
 * `Broadcast.tsx` decides how to say it.
 *
 * ## The camera is opened before publishing, and stays open after stopping
 *
 * A host sets up minutes before they go live: they need to see themselves, aim the camera, and
 * watch the microphone move. So `open()` acquires devices and shows a local preview with no stage
 * involved, and `start()` publishes what is already open. That separation is what makes the
 * pre-flight check (`R-HOST-11`) something a host passes *before* anything is live, rather than a
 * report on a broadcast already in progress.
 *
 * ## Reconnection is a fresh token and the same stage
 *
 * `R-VID-6` is a hard requirement — *a host walking between rooms, or whose phone switches from
 * wifi to mobile data, reconnects into the same stream* — and `R-SHOW-3` makes it consequential:
 * a dropped stream does not end a show, and that guarantee is worthless if reconnecting starts a
 * new one. The API answers with a token for the show's existing stage every time, so rejoining is
 * the same three lines as joining and the show never notices.
 */

/** Between publish-quality samples. Slower than the viewer's: nothing here has a 2s budget. */
const SAMPLE_MS = 1_000;

const REJOIN_BACKOFF_MS = [500, 1_000, 2_000, 5_000, 10_000];

/**
 * This tab's device id for a show (`R-VID-39`), kept in `sessionStorage` so that **a reload is the
 * same device**.
 *
 * A host who reloads the broadcasting phone mid-show is reconnecting, not arriving: with a fresh id
 * the server would refuse the phone its own show until IVS noticed the old page had gone, and the
 * panel no longer retries a refusal. `sessionStorage` is per tab, so another tab or another device
 * is still a different device. A *duplicated* tab copies it and would count as the same device —
 * the one way round this, and a deliberate act on a machine that already has the camera.
 *
 * Storage can be refused (a private window, a locked-down browser); then the id lasts as long as
 * the page, which is the behaviour without this.
 */
function deviceFor(showId: string): string {
  const key = `lc-broadcast-device:${showId}`;
  try {
    const kept = sessionStorage.getItem(key);
    if (kept) return kept;
    const made = crypto.randomUUID();
    sessionStorage.setItem(key, made);
    return made;
  } catch {
    return crypto.randomUUID();
  }
}

export type PublishState =
  "idle" | "starting" | "publishing" | "reconnecting" | "failed";

export interface Publisher {
  /** `R-HOST-10`, decided before anything is opened. */
  support: Support;
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
  cameraId: string | undefined;
  microphoneId: string | undefined;
  choose: (kind: "camera" | "microphone", deviceId: string) => void;
  /**
   * Attach to a muted `<video>` — the host's own camera, never routed through the stage.
   *
   * A **callback ref** rather than a ref object, and not for style: a ref returned from a hook
   * spreads through everything that reads the object it came back in, and React's own lint rules
   * then flag every property read of that object during render as a ref access. Handing back a
   * setter keeps the ref private to the hook, which is where it belongs anyway.
   */
  attachPreview: (element: HTMLVideoElement | null) => void;
  /** True once *either* device is open. A camera that will not open does not stop a microphone. */
  opened: boolean;
  /** A live camera track. `R-HOST-11`'s *camera selected and working*, as a fact. */
  cameraOn: boolean;
  /** A live microphone track. Working is `heard`, below — a track alone proves nothing. */
  microphoneOn: boolean;
  /**
   * Why each one would not open, or `undefined` where it did.
   *
   * Separate, because they fail separately and for different reasons: a blocked camera and a
   * missing microphone on the same machine are two sentences with two different remedies, and one
   * request asking for both can only ever produce one message about the request.
   */
  cameraFailure: DeviceFailure | undefined;
  microphoneFailure: DeviceFailure | undefined;
  /** 0 to 1, from the microphone itself. The evidence that a microphone is *working*. */
  level: number;
  /** True once the microphone has actually made a sound. A silent mic is a broken mic. */
  heard: boolean;
  /**
   * Whether the level meter can measure anything at all.
   *
   * False while the browser's `AudioContext` is suspended, which is its state until the page has
   * been interacted with. The microphone is unaffected — it is captured and published regardless —
   * so this exists to stop the panel reporting *not heard* when the truth is *not yet measurable*.
   */
  meterAwake: boolean;
  /**
   * The track exists and is delivering nothing, because something outside the browser has muted
   * it — on a laptop, usually a key on the keyboard. Worth its own state because **WebRTC publishes
   * the silence**: this is not only a dead meter, it is a show nobody can hear.
   */
  microphoneMuted: boolean;
  state: PublishState;
  /**
   * What the publish is actually doing: resolution, frame rate, bitrate, loss, round trip.
   * `undefined` until the second sample, because the rates are deltas. Upstream only — see
   * `stats.ts` for what these figures are not.
   */
  stats: PublishStats | undefined;
  /**
   * How many participants are subscribed to this show's stage, from IVS.
   *
   * The host's answer to *is anybody watching*, and it costs nothing — the SDK reports it on an
   * event. This is the **panel's** figure; the customer-facing viewer count (`R-ANL-1`) is the
   * room's, from Redis, and is what the show page shows as *watching*. `undefined` before IVS has
   * said.
   */
  subscribers: number | undefined;
  /** `R-HOST-13` — the tab is not visible, which degrades or drops a publish. */
  backgrounded: boolean;
  /**
   * Whether the preview should be mirrored: true for a self view, false for a back camera.
   *
   * A mirrored view of a product on a table is simply wrong — packaging text reads backwards — and
   * a *self* view that is not mirrored is unexpectedly hard to aim. What is published is never
   * mirrored either way.
   */
  mirrored: boolean;
  /**
   * Whether the browser is holding the screen awake while publishing.
   *
   * **On a phone this is the difference between a show and a two-minute show**: Android locks the
   * screen after its timeout and a locked screen stops the publish. `false` means either the
   * browser has no Screen Wake Lock API or it refused, and the host has to set the timeout
   * themselves — which the panel then tells them.
   */
  screenAwake: boolean;
  failure: string | undefined;
  /** Asks for both devices. Safe to call again — it is also the retry after a failure. */
  open: () => Promise<void>;
  start: () => Promise<void>;
  stop: () => void;
  /** This panel's device id (`R-VID-39`), which the pre-flight poll sends with it. */
  device: string;
  /**
   * Clears a refusal (`R-VID-39`) and nothing else — called by the panel when the server's answer
   * about another device changes, which is when the refusal stops being the news.
   */
  forgetRefusal: () => void;
}

/**
 * `autoOpen` decides whether the devices are opened without being asked for.
 *
 * It is the caller's to decide because only the caller knows whether **another device is already
 * broadcasting this show** — the arrangement `docs/runbooks/video.md` §2 describes, where an Android phone is
 * the camera and a laptop drives the running order. On that laptop, opening a camera is at best a
 * webcam light coming on for no reason and at worst an invitation to publish a second picture into
 * the same show.
 */
export interface PublisherOptions {
  autoOpen: boolean;
}

export function usePublisher(showId: string, options: PublisherOptions): Publisher {
  const preview = useRef<HTMLVideoElement | null>(null);
  const [supported, setSupported] = useState<Support>({ ok: true });
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState<string | undefined>(undefined);
  const [microphoneId, setMicrophoneId] = useState<string | undefined>(undefined);
  const [cameraOn, setCameraOn] = useState(false);
  const [microphoneOn, setMicrophoneOn] = useState(false);
  const [cameraFailure, setCameraFailure] = useState<DeviceFailure | undefined>(
    undefined,
  );
  const [microphoneFailure, setMicrophoneFailure] = useState<DeviceFailure | undefined>(
    undefined,
  );
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState(false);
  const [meterAwake, setMeterAwake] = useState(false);
  const [microphoneMuted, setMicrophoneMuted] = useState(false);
  const [mirrored, setMirrored] = useState(true);
  const [state, setState] = useState<PublishState>("idle");
  const [stats, setStats] = useState<PublishStats | undefined>(undefined);
  const [subscribers, setSubscribers] = useState<number | undefined>(undefined);
  const [backgrounded, setBackgrounded] = useState(() => document.hidden);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  /**
   * Everything that is not React state, because none of it should cause a render and all of it has
   * to be torn down exactly once. Held in one ref so the cleanup path is a single object.
   */
  /**
   * This panel's id for `R-VID-39` — one device broadcasts a show at a time. Sent with every token
   * request and every pre-flight poll, so the server can tell a reconnection from this panel
   * (always allowed) from a second device (refused). See `deviceFor` for why it survives a reload.
   * State rather than a field on `kit`, because the panel reads it during render.
   */
  const [device] = useState(() => deviceFor(showId));

  const kit = useRef({
    camera: undefined as MediaStreamTrack | undefined,
    microphone: undefined as MediaStreamTrack | undefined,
    audio: undefined as AudioContext | undefined,
    meter: undefined as number | undefined,
    stage: undefined as Stage | undefined,
    streams: [] as LocalStageStream[],
    sampler: undefined as ReturnType<typeof setInterval> | undefined,
    /**
     * The level meter's audio graph, **held so that nothing in it is collected**. See the long note
     * where it is built: a `MediaStreamAudioSourceNode` that nothing references is one Chrome may
     * garbage-collect, and collecting it silences the audio through it.
     */
    graph: undefined as
      { stream: MediaStream; source: AudioNode; analyser: AnalyserNode } | undefined,
    /** One analyser for the life of the context, replaced only with it. */
    analyser: undefined as AnalyserNode | undefined,
    /** The previous statistics reading, which is what makes a rate out of a counter. */
    reading: undefined as PublishSnapshot | undefined,
    /** Removes the interaction listeners, and the track's own mute listeners, that go with it. */
    releaseWake: undefined as (() => void) | undefined,
    /**
     * Which device-opening run is the current one.
     *
     * Two can overlap — the automatic open on mount and a host pressing the button, or two presses
     * — and each one begins by stopping the tracks it finds. Without this, the *slower* run's
     * continuation writes its track into `held` after the faster run has already stopped it, and
     * the panel is left holding a dead camera it believes is live. Same counter, same reasoning as
     * `generation` below.
     */
    opening: 0,
    rejoin: undefined as ReturnType<typeof setTimeout> | undefined,
    attempts: 0,
    stopped: false,
    /** Set once the host has asked to publish, so a reconnection knows it is still wanted. */
    wanted: false,
    /**
     * Whether `failure` is `R-VID-39`'s refusal — *another device is broadcasting this show* — rather
     * than something wrong with this device. The panel clears it once the server's answer moves on,
     * which is not something it may do to a real failure. A ref, so `forgetRefusal` keeps one
     * identity: as state it would change on the refusal itself, and the panel's effect would clear
     * the message the moment it appeared.
     */
    refused: false,
    /** So the automatic open below happens once, not twice under React's strict double-mount. */
    autoOpened: false,
    /**
     * Which publish attempt is the current one.
     *
     * A rejoin takes a token round trip and a dynamic import, and two different things can ask for
     * one in that window — the connection-state handler and a failed token fetch. An attempt claims
     * the next number and abandons itself if another has been claimed since, which stops two
     * `Stage` objects publishing the same camera and stops an abandoned one's handlers from
     * reporting on a connection that has been replaced.
     */
    generation: 0,
  });

  // `R-HOST-10` decided on mount, and before the panel offers a single control. The SDK is
  // imported for one function, which is worth the request: the alternative is telling a host their
  // camera is broken when the truth is that their browser cannot publish.
  useEffect(() => {
    let live = true;
    void import("amazon-ivs-web-broadcast").then((sdk) => {
      if (live) setSupported(support(browserFacts(sdk.isSupported())));
    });
    return () => {
      live = false;
    };
  }, []);

  /** `R-HOST-13`. A backgrounded tab is throttled by the browser and a sleeping machine drops. */
  useEffect(() => {
    const onVisibility = () => setBackgrounded(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  /**
   * **Holds the screen awake while publishing**, which on a phone is the difference between a show
   * and a two-minute show: Android locks the screen after its timeout, and a locked screen stops
   * the publish outright. `R-HOST-13` asks the panel to *warn* about a machine that is likely to
   * sleep; where the browser will let us, preventing it is better than warning about it.
   *
   * The hook is shared with the customer's room, where a sleeping screen costs a viewer the lot
   * they were bidding on rather than the whole broadcast. Its two load-bearing details — a lock is
   * released whenever the page is hidden and must be re-acquired, and the request can simply be
   * refused — are documented there.
   *
   * `screenAwake` is why this returns anything: a host whose screen is *not* being held needs to
   * know the timeout is theirs to set.
   */
  const screenAwake = useWakeLock(state === "publishing");

  const stop = useCallback(() => {
    const held = kit.current;
    // Given up at the server, so another device may broadcast at once rather than after IVS
    // notices this one has gone (`R-VID-39`). Best effort: a release that fails leaves the claim to
    // lapse that way instead.
    if (held.wanted) void video.release(showId, device).catch(() => undefined);
    held.wanted = false;
    if (held.rejoin) clearTimeout(held.rejoin);
    held.rejoin = undefined;
    try {
      held.stage?.leave();
    } catch {
      // Leaving a stage that has already gone is not a failure worth reporting.
    }
    held.stage = undefined;
    held.streams = [];
    held.attempts = 0;
    held.reading = undefined;
    setState("idle");
    setStats(undefined);
    setSubscribers(undefined);
  }, [showId, device]);

  /** Releases the camera, the microphone and the meter. The light goes out; that matters. */
  const close = useCallback(() => {
    const held = kit.current;
    if (held.meter !== undefined) cancelAnimationFrame(held.meter);
    held.meter = undefined;
    held.releaseWake?.();
    held.releaseWake = undefined;
    held.graph?.source.disconnect();
    held.graph = undefined;
    held.analyser = undefined;
    void held.audio?.close();
    held.audio = undefined;
    held.camera?.stop();
    held.microphone?.stop();
    held.camera = undefined;
    held.microphone = undefined;
    if (preview.current) preview.current.srcObject = null;
    setCameraOn(false);
    setMicrophoneOn(false);
    setMirrored(true);
    setLevel(0);
    setHeard(false);
    setMeterAwake(false);
    setMicrophoneMuted(false);
  }, []);

  /**
   * Teardown, and the flag that says whether this hook is still alive.
   *
   * **`stopped` is set on the way in as well as on the way out, and that symmetry is the whole
   * point.** `kit` is a ref, so it outlives a remount — and React's StrictMode deliberately mounts,
   * tears down, and mounts again. Setting `stopped = true` in the cleanup and never clearing it
   * leaves the flag permanently raised from the second mount onwards, so every publish attempt
   * returns at its first line and *Start broadcasting* does nothing at all. The camera preview still
   * works, because opening devices does not consult the flag, which makes it look like a publishing
   * problem rather than a lifecycle one. A production build has no StrictMode and is unaffected —
   * so this is invisible to `vite build` and obvious to the first person to press the button.
   */
  useEffect(() => {
    const held = kit.current;
    held.stopped = false;
    return () => {
      held.stopped = true;
      if (held.sampler) clearInterval(held.sampler);
      stop();
      close();
    };
  }, [stop, close]);

  /** Rebuilds the preview and the meter from whatever tracks are currently held. */
  const attachLocal = useCallback(() => {
    const held = kit.current;

    /**
     * **The preview gets the camera and nothing else.**
     *
     * Do not hand it the microphone as well. It would be pointless — the element is muted, so an
     * audio track attached to it is rendered nowhere — and it silences the level meter. A track
     * being consumed by an `HTMLMediaElement` does not reliably also deliver to a
     * `MediaStreamAudioSourceNode` reading the same track in Chromium: the element takes the
     * audio, the analyser gets zeros, and every other diagnostic looks perfect — a running context,
     * a live unmuted track, a graph that is held — which makes it easy to fix in the wrong place.
     *
     * The audio track has two real consumers: the analyser here, and the stage when publishing.
     * The preview was never one of them.
     */
    if (preview.current) {
      preview.current.srcObject = held.camera ? new MediaStream([held.camera]) : null;
      // Belt and braces on top of carrying no audio track at all: an unmuted preview of your own
      // microphone through your own speakers is a feedback loop.
      preview.current.muted = true;
      void preview.current.play().catch(() => undefined);
    }

    /**
     * The level meter — `R-HOST-11`'s *microphone selected and working*. A device in a list proves
     * a device exists; a moving bar proves it is picking up the person about to present.
     *
     * ## The source node is built only once the context is genuinely running
     *
     * This is the order that matters, and getting it wrong makes the meter read silence on every
     * refresh. A browser starts an `AudioContext` **suspended** unless a user gesture
     * preceded it, and the panel opens devices by itself when permission is already granted — so on
     * a refresh there is no gesture and the context begins suspended. Calling `resume()` moves it to
     * `running`, and it reports `running` — but a `MediaStreamAudioSourceNode` created while it was
     * suspended does not reliably start pulling from the track afterwards. Every diagnostic then
     * reads perfectly and the analyser still sees zeros.
     *
     * So `wire()` refuses to build the source until `state === "running"`, and it is called from
     * three places — after the resume attempt, on the context's own state change, and on the first
     * interaction anywhere on the page. Whichever happens first wins; it is idempotent.
     *
     * ## One context, held, for the life of the hook
     *
     * Not one per open. A browser caps how many an origin may have, closing is asynchronous, and
     * every node in the graph has to be referenced for its lifetime or it becomes eligible for
     * collection — and a collected `MediaStreamAudioSourceNode` silences the audio through it.
     * Reusing one context and replacing only the source removes all of that as a variable.
     *
     * There is deliberately **no connection to the destination**. An analyser needs none, and a
     * subgraph whose output is silent is one an implementation may legitimately stop pulling.
     */
    if (held.meter !== undefined) cancelAnimationFrame(held.meter);
    held.meter = undefined;
    held.graph?.source.disconnect();
    held.graph = undefined;
    held.releaseWake?.();
    held.releaseWake = undefined;

    if (!held.microphone) return;

    const track = held.microphone;
    const context = (held.audio ??= new AudioContext());
    const analyser = (held.analyser ??= (() => {
      const node = context.createAnalyser();
      node.fftSize = 512;
      return node;
    })());
    const samples = new Float32Array(analyser.fftSize);

    /**
     * **A track can be muted outside the browser**, and on a laptop that is a key on the keyboard.
     * `getUserMedia` succeeds, a track exists, `track.muted` is true, and **WebRTC publishes the
     * silence** — so it is not only a dead meter but a show nobody can hear. Its own state, because
     * *say something* is useless advice for it.
     */
    const watchMute = () => setMicrophoneMuted(track.muted);
    track.addEventListener("mute", watchMute);
    track.addEventListener("unmute", watchMute);
    watchMute();

    let lastRms = 0;
    let lastPeak = 0;

    const tick = () => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      let peak = 0;
      for (const value of samples) {
        sum += value * value;
        const magnitude = Math.abs(value);
        if (magnitude > peak) peak = magnitude;
      }
      lastRms = Math.sqrt(sum / samples.length);
      lastPeak = peak;

      setLevel(meterScale(lastRms));
      if (audible(lastRms)) setHeard(true);
      held.meter = requestAnimationFrame(tick);
    };

    /** Builds the source and starts sampling. Idempotent, and a no-op until the context runs. */
    const wire = () => {
      if (held.graph || context.state !== "running") return;
      const stream = new MediaStream([track]);
      const source = context.createMediaStreamSource(stream);
      source.connect(analyser);
      held.graph = { stream, source, analyser };
      if (held.meter === undefined) held.meter = requestAnimationFrame(tick);

      if (import.meta.env.DEV) {
        /**
         * **What the meter actually reads, and what that means**, once a second until it has an
         * answer.
         *
         * Five separate things have presented on this panel as *the microphone is not working*, and
         * every one of them looked identical from the outside: a bar that does not move. Raw numbers
         * are not enough — printing a few lines of them and stopping reads as the meter dying
         * rather than as the probe finishing. So it prints a
         * **verdict**, and it keeps looking until it hears something or gives up at ten seconds.
         */
        let checks = 0;
        let previous = context.currentTime;
        const probe = setInterval(() => {
          checks += 1;
          const advancing = context.currentTime > previous;
          previous = context.currentTime;

          const verdict =
            held.meter === undefined
              ? "not sampling — the animation loop is not running"
              : context.state !== "running"
                ? `the audio context is ${context.state}`
                : !advancing
                  ? "the audio context is not processing — its clock is not advancing"
                  : lastPeak > 0
                    ? "reading audio"
                    : "the analyser is receiving digital silence";

          console.debug("[broadcast] meter", {
            second: checks,
            verdict,
            peakDbfs: Number(decibels(lastPeak).toFixed(1)),
            rmsDbfs: Number(decibels(lastRms).toFixed(1)),
            bar: `${Math.round(meterScale(lastRms) * 100)}%`,
            currentTime: Number(context.currentTime.toFixed(3)),
            contextState: context.state,
            trackMuted: track.muted,
          });

          if (lastPeak > 0 || checks >= 10) clearInterval(probe);
        }, 1_000);
      }
    };

    const watchState = () => {
      setMeterAwake(context.state === "running");
      wire();
    };
    context.onstatechange = watchState;

    void context
      .resume()
      .catch(() => undefined)
      .finally(watchState);
    watchState();

    // The first interaction anywhere on the page is what a browser will accept as permission to
    // start audio. Resuming an already-running context is a no-op, so these can be blunt.
    const wake = () =>
      void context
        .resume()
        .catch(() => undefined)
        .finally(watchState);
    window.addEventListener("pointerdown", wake);
    window.addEventListener("keydown", wake);
    held.releaseWake = () => {
      window.removeEventListener("pointerdown", wake);
      window.removeEventListener("keydown", wake);
      track.removeEventListener("mute", watchMute);
      track.removeEventListener("unmute", watchMute);
    };

    if (import.meta.env.DEV) {
      console.debug("[broadcast] microphone", {
        label: track.label,
        readyState: track.readyState,
        muted: track.muted,
        enabled: track.enabled,
        contextState: context.state,
        sampleRate: context.sampleRate,
      });
    }
  }, []);

  /**
   * Asks for the camera and the microphone, **as two separate requests**, and reports on each.
   *
   * The separation is the whole point and it is worth restating here because the combined version
   * looks correct and is not: `getUserMedia({ video, audio })` fails as a unit, so a laptop whose
   * microphone is switched off in the operating system answers `NotFoundError` for the *pair* —
   * no permission prompt, no camera, and nothing in the message saying which device was missing.
   * A host is then looking at two disabled pickers and an error about a device they do have.
   *
   * Devices are enumerated **afterwards regardless of the outcome**, so that a machine with a
   * blocked camera still fills its microphone picker, and a second attempt has a list to choose
   * from. (`Broadcast.tsx` also enumerates on mount, before any permission, which is what lets the
   * checklist say *no camera found* rather than *allow the camera*.)
   */
  const open = useCallback(
    async (camera?: string, microphone?: string) => {
      const held = kit.current;
      if (held.stopped) return;
      const mine = ++held.opening;
      const current = () => !held.stopped && held.opening === mine;
      setFailure(undefined);

      held.camera?.stop();
      held.microphone?.stop();
      held.camera = undefined;
      held.microphone = undefined;

      // Sequential rather than concurrent, on purpose. Chrome shows one permission prompt per
      // request and two at once are stacked on top of each other; asking in turn means the host
      // answers "camera?" and then "microphone?", which is also the order they are listed in.
      const eye = await openCamera(camera);
      if (!current()) {
        // Superseded while we waited. The track we just acquired belongs to nobody, and leaving it
        // open would hold the camera — and its indicator light — for the life of the page.
        if (eye.ok) eye.track.stop();
        return;
      }
      if (eye.ok) held.camera = eye.track;
      setCameraOn(eye.ok);
      setCameraFailure(eye.ok ? undefined : eye.why);

      const ear = await openMicrophone(microphone);
      if (!current()) {
        if (ear.ok) ear.track.stop();
        return;
      }
      if (ear.ok) held.microphone = ear.track;
      setMicrophoneOn(ear.ok);
      setMicrophoneFailure(ear.ok ? undefined : ear.why);

      setCameraId(held.camera?.getSettings().deviceId);
      setMicrophoneId(held.microphone?.getSettings().deviceId);
      setMirrored(isSelfView(held.camera));
      if (!ear.ok) setHeard(false);

      const lists = await listDevices();
      if (!current()) return;
      setCameras(lists.cameras);
      setMicrophones(lists.microphones);

      attachLocal();
    },
    [attachLocal],
  );

  /**
   * Publishes what is open, joining the show's stage.
   *
   * Called by the host once, and by the reconnection path however many times it takes. Both paths
   * ask the API for a token first, which is what makes them the same code.
   */
  const start = useCallback(async () => {
    // A press of the button starts from scratch: no inherited backoff from a previous failure, and
    // the badge reads *Starting* rather than *Reconnecting*, which is what the host just did.
    kit.current.attempts = 0;
    // A new attempt: whatever refused the last one is not the answer to this one.
    setFailure(undefined);
    kit.current.refused = false;
    /**
     * One attempt, and the recursion that retries it.
     *
     * A local function rather than `start` calling itself: a `useCallback` that recurses on its
     * own binding is a reference to a variable inside its own initialiser, which React's lint
     * rules reject and which is genuinely fragile — the identity it captures is the one from the
     * render that created it. Keeping the retry inside the closure means every attempt calls the
     * same function it is part of, however many times a network transition asks for one.
     */
    async function attempt(): Promise<void> {
      const held = kit.current;
      if (held.stopped) return;
      const mine = ++held.generation;
      /** True while this attempt is still the current one, and still wanted. */
      const current = () => !held.stopped && held.wanted && held.generation === mine;
      if (!held.camera && !held.microphone) {
        setFailure(undefined);
        await open(cameraId, microphoneId);
        // Still nothing to send. One device is enough — a voice with no picture is a degraded show
        // and no picture and no voice is not a show at all.
        if (!kit.current.camera && !kit.current.microphone) {
          setState("failed");
          return;
        }
      }

      held.wanted = true;
      setState(held.attempts === 0 ? "starting" : "reconnecting");

      try {
        held.stage?.leave();
      } catch {
        // As above: an already-closed stage is not an error on the way to a new one.
      }
      held.stage = undefined;

      const wait =
        REJOIN_BACKOFF_MS[Math.min(held.attempts, REJOIN_BACKOFF_MS.length - 1)]!;
      if (held.attempts > 0) {
        await new Promise<void>((resolve) => {
          held.rejoin = setTimeout(resolve, wait);
        });
        if (!current()) return;
      }
      held.attempts += 1;

      let token: string;
      try {
        token = (await video.broadcast(showId, device)).broadcast.token;
      } catch (error) {
        if (!current()) return;
        setFailure(error instanceof Error ? error.message : String(error));
        /**
         * **Another device has the camera** (`R-VID-39`) — not a failure of this one. The badge goes
         * back to *Not broadcasting* rather than red *Could not start*, which nothing would ever have
         * reset: the panel's own state only moves when this device does something, and the next thing
         * to happen is the other device stopping. The message stays until the panel's poll catches up
         * (`forgetRefusal`).
         */
        const refusal =
          error instanceof ApiError &&
          error.status === 409 &&
          error.body.reason === "broadcasting-elsewhere";
        held.refused = refusal;
        setState(refusal ? "idle" : "failed");
        /**
         * **A refusal ends it.** A `4xx` is the API saying no rather than failing to answer — most
         * often `R-VID-39`'s *another device is broadcasting this show*, or a show that has ended —
         * and asking again every ten seconds changes neither. It used to be retried like anything
         * else, which is how a panel that lost the argument would have kept knocking for the rest of
         * the show. The host presses *Start broadcasting* again once the other device has stopped.
         */
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          held.wanted = false;
          return;
        }
        // Retried rather than abandoned: the usual cause is a moment of API unavailability, and a
        // host who pressed *start broadcasting* has said what they want.
        void attempt();
        return;
      }

      const sdk = await import("amazon-ivs-web-broadcast");
      if (!current()) return;

      const streams: LocalStageStream[] = [];
      if (held.camera) {
        /**
         * **Simulcast, on — with fewer layers on a phone.**
         *
         * On is not optional: the SDK defaults it off, and off puts `R-VID-11` out of reach —
         * *adaptive bitrate is required, a viewer on a poor connection drops to a lower quality
         * rather than buffering* — because with one layer there is nothing to drop to and a weak
         * viewer stalls instead, which the requirement calls the worse outcome.
         *
         * The **count** is the trade. Each layer is another encoder, and the broadcasting device
         * here is an Android phone (`docs/runbooks/video.md` §2) running for the length of a show, on a
         * battery, in somebody's hand — and AWS warns about mobile-web publishing performance
         * specifically. Two layers keep the requirement satisfied, since there is still something to
         * step down to, at two-thirds of the encoding cost; a laptop takes the SDK's default three.
         *
         * 720p is the ceiling in this mode whatever is asked for (spec §12.6), so the top layer is
         * the stream and the second is the fallback.
         */
        const mobile = isMobile(browserFacts(true));
        streams.push(
          new sdk.LocalStageStream(held.camera, {
            simulcast: mobile
              ? {
                  enabled: true,
                  layers: [
                    sdk.SimulcastLayerPresets.DEFAULT_720,
                    sdk.SimulcastLayerPresets.DEFAULT_360,
                  ],
                }
              : { enabled: true },
          }),
        );
      }
      if (held.microphone) streams.push(new sdk.LocalStageStream(held.microphone));
      held.streams = streams;

      const stage = new sdk.Stage(token, {
        stageStreamsToPublish: () => kit.current.streams,
        shouldPublishParticipant: () => true,
        // A host does not watch the show they are presenting. Subscribing would cost a slot, some
        // CPU and an echo; showing viewers' picture (`R-VID-9`) is the stream monitor's job, which
        // `ShowPage` withholds from the publishing browser, and it needs a `SUBSCRIBE` token this
        // one deliberately does not carry.
        shouldSubscribeToParticipant: () => sdk.SubscribeType.NONE,
      });

      stage.on(
        sdk.StageEvents.STAGE_PARTICIPANT_PUBLISH_STATE_CHANGED,
        (participant, next) => {
          if (!participant.isLocal || !current()) return;
          if (next === sdk.StageParticipantPublishState.PUBLISHED) {
            kit.current.attempts = 0;
            setState("publishing");
            setFailure(undefined);
          }
          if (next === sdk.StageParticipantPublishState.ERRORED)
            setState("reconnecting");
        },
      );

      /**
       * How many people are subscribed, straight from IVS. The host's *is anybody watching*, and
       * the only figure in this panel that is about the audience rather than the machine.
       */
      stage.on(sdk.StageEvents.STAGE_SUBSCRIBER_COUNT_CHANGED, (count) => {
        if (current()) setSubscribers(count);
      });

      stage.on(sdk.StageEvents.STAGE_CONNECTION_STATE_CHANGED, (next) => {
        if (!current()) return;
        if (
          next === sdk.StageConnectionState.DISCONNECTED ||
          next === sdk.StageConnectionState.ERRORED
        ) {
          // `R-VID-6`, and `R-SHOW-3` behind it: the show stays live and the host comes back into
          // the same stream. Nothing about the show changes here — only this connection.
          setState("reconnecting");
          void attempt();
        }
      });

      held.stage = stage;

      try {
        await stage.join();
        // A join that lands after this attempt was superseded is a second publisher on the stage,
        // sending the same camera. Twelve are permitted (spec §12.6) and one is wanted.
        if (!current()) stage.leave();
      } catch (error) {
        if (!current()) return;
        console.error("Could not join the show's stage to publish", error);
        setState("reconnecting");
        void attempt();
      }
    }

    await attempt();
  }, [showId, device, cameraId, microphoneId, open]);

  /**
   * Opens the devices on mount **when the browser has already granted them**, and asks for nothing
   * when it has not.
   *
   * Two things are being balanced. A host who allowed the camera last week should see themselves
   * the moment they open a scheduled show — a pre-flight check that needs a button press first is
   * a step that exists only because the code needed one. But somebody opening the same show only to
   * drive the running order should not be asked for a camera unprompted — an unprompted permission
   * dialogue on a page that was opened to press *Open lot* is exactly the kind of thing people learn
   * to dismiss. (A moderator never gets this far: `ShowPage` renders no `Broadcast` for them,
   * `R-HOST-30`.)
   *
   * So: already granted, open it. Otherwise wait to be asked, and let the checklist say plainly
   * that nothing has been asked for yet.
   */
  useEffect(() => {
    // Captured on the way in, so the cleanup writes to the object this run reasoned about rather
    // than reading the ref again at teardown. It is the same object either way — `kit` is never
    // reassigned — but stating it keeps the rule that a cleanup does not dereference a ref.
    const held = kit.current;
    if (!options.autoOpen) return;
    let alive = true;
    void alreadyAllowed().then((granted) => {
      if (!alive || !granted || held.autoOpened) return;
      held.autoOpened = true;
      void open();
    });
    return () => {
      alive = false;
      // Released, for the same reason `stopped` is: the teardown above closes the devices, so a
      // remount that still believed it had already opened them would show a host a dead preview.
      held.autoOpened = false;
    };
  }, [open, options.autoOpen]);

  /**
   * Changing camera or microphone mid-setup, or mid-broadcast.
   *
   * Reopening the devices and republishing is the whole of it. A live publish is rejoined rather
   * than patched: `refreshStrategy` would swap the tracks in place and is the more elegant call,
   * but it is one more code path to be wrong on the night, and a host changing camera during a
   * show is rare enough that half a second of black is the cheaper trade. `R-VID-6` guarantees the
   * stream comes back to the same stage either way.
   */
  const choose = useCallback(
    (kind: "camera" | "microphone", deviceId: string) => {
      void open(
        kind === "camera" ? deviceId : cameraId,
        kind === "microphone" ? deviceId : microphoneId,
      ).then(() => {
        if (kit.current.wanted) void start();
      });
    },
    [cameraId, microphoneId, open, start],
  );

  /**
   * Publish quality, once a second: is the upstream keeping up?
   *
   * `qualityLimitationReason` is WebRTC's own answer and it is the honest one — it says the
   * encoder is being held back, and by what. A synthetic speed test before publishing (`R-HOST-11`,
   * M2.6) measures a different network at a different moment; this measures the one carrying the
   * show.
   */
  useEffect(() => {
    if (state !== "publishing") return;
    const timer = setInterval(() => {
      const stream = kit.current.streams.find(
        (one) => one.mediaStreamTrack.kind === "video",
      );
      if (!stream) return;
      void stream
        .requestRTCStats()
        .then((report) => {
          const next = readPublishStats(report, kit.current.reading, performance.now());
          kit.current.reading = next.snapshot;
          setStats(next);
        })
        .catch(() => undefined);
    }, SAMPLE_MS);
    kit.current.sampler = timer;
    return () => clearInterval(timer);
  }, [state]);

  /**
   * The callback ref. Stable, so React does not detach and re-attach the element on every render —
   * and it re-attaches the live stream when the element arrives, which is what makes the preview
   * survive the card being hidden and shown again.
   */
  const attachPreview = useCallback((element: HTMLVideoElement | null) => {
    preview.current = element;
    // The camera alone, for the reason spelled out in `attachLocal`: an audio track handed to a
    // media element is one the level meter's analyser may never see.
    const camera = kit.current.camera;
    if (!element || !camera) return;
    element.srcObject = new MediaStream([camera]);
    element.muted = true;
    void element.play().catch(() => undefined);
  }, []);

  return {
    support: supported,
    cameras,
    microphones,
    cameraId,
    microphoneId,
    choose,
    attachPreview,
    // Either device is enough to be "open": a host with no camera can still broadcast a voice,
    // which is the half `R-VID-7` says matters most.
    opened: cameraOn || microphoneOn,
    cameraOn,
    microphoneOn,
    cameraFailure,
    microphoneFailure,
    level,
    heard,
    meterAwake,
    microphoneMuted,
    state,
    // Derived rather than cleared when publishing stops: a stale warning outliving the publish it
    // described is exactly the kind of thing a host reads as current.
    // Both only mean something while publishing, and a figure that outlives the stream it
    // described is one a host will read as current.
    stats: state === "publishing" ? stats : undefined,
    subscribers: state === "publishing" ? subscribers : undefined,
    backgrounded,
    mirrored,
    screenAwake,
    failure,
    open: useCallback(
      () => open(cameraId, microphoneId),
      [open, cameraId, microphoneId],
    ),
    start,
    stop,
    /** This panel's device id (`R-VID-39`), which the pre-flight poll sends with it. */
    device,
    forgetRefusal: useCallback(() => {
      if (!kit.current.refused) return;
      kit.current.refused = false;
      setFailure(undefined);
    }, []),
  };
}
